import { Application } from "@core-ts/Contexts/Application"
import { log } from "@core-ts/Contexts/Context"
import { MutexMap } from "@core-ts/Map/MutexMap"
import { Round } from "@core-ts/Math"

// Don't make a cache that is global to all instances, it is too dangerous and never a real use case

export abstract class CacheBase<K, V, StateType = string> {
    constructor(readonly name: string, readonly maximumDataValiditySeconds: number | null = null, readonly maximumCacheSize: number | null = null) {
        Application.addStatisticsHook(this, () => { this.logStatistics() })
        Application.addGarbageCollectHook(this, () => { this.garbageCollect() })
    }

    private objectEntryLocks = new MutexMap<string>()

    requests = 0
    hits = 0
    protected cacheStorage = new Map<string, CacheStorage<V, StateType>>()

    protected async internalGet(keyAsObject: K, fn: () => Promise<V>, stateWhenValid: StateType | null = null, cacheTimeOverrideSeconds?: number): Promise<V> {
        const keyAsString = JSON.stringify(keyAsObject)

        const entryMutex = await this.objectEntryLocks.getOrCreateMutex(keyAsString)

        return await entryMutex.runExclusive(async () => {
            this.requests++
            const cachedResult = this.cacheStorage.get(keyAsString)
            if (cachedResult !== undefined) {
                if (stateWhenValid === null || cachedResult.stateWhenValid === stateWhenValid) {
                    if ((this.maximumDataValiditySeconds === null || (Date.now() - cachedResult.timeSet) < this.maximumDataValiditySeconds * 1000)
                        && (cacheTimeOverrideSeconds === undefined || (Date.now() - cachedResult.timeSet) < cacheTimeOverrideSeconds * 1000)
                    ) {
                        //log.perf("Cache: Cache hit for key: " + keyAsString)
                        cachedResult.timeUsed = Date.now()
                        this.hits++
                        return cachedResult.value
                    }
                } else {
                    //log.debug("Cache: Cache miss (state when valid) for key: " + keyAsString)
                    // No need to delete it really since we are going to overwrite it anyway
                }
            } else {
                //log.debug("Cache: Cache miss (undefined) for key: " + keyAsString)
            }

            // Create the new result
            const result = await fn()
            // Delete it an insert it again since that will place it last in the list. That will keep our map in the order of latest access
            if(this.cacheStorage.has(keyAsString))
                this.cacheStorage.delete(keyAsString)

            // If we have limited the cache size, we need to remove the oldest entry
            if(this.maximumCacheSize !== null && this.cacheStorage.size >= this.maximumCacheSize) {
                const entry = this.cacheStorage.keys().next().value
                if(entry !== undefined)
                    this.cacheStorage.delete(entry)
            }

            this.cacheStorage.set(keyAsString, { value: result, timeSet: Date.now(), timeUsed: Date.now(), stateWhenValid: stateWhenValid })

            return result
        })
    }

    public clear(): void {
        this.cacheStorage.clear()
        this.objectEntryLocks = new MutexMap<string>()
    }

    public delete(keyAsObject: K): void {
        const keyAsString = JSON.stringify(keyAsObject)

        this.cacheStorage.delete(keyAsString)
        this.objectEntryLocks.delete(keyAsString) 
    }

    public get size(): number {
        return this.cacheStorage.size
    }

    private garbageCollect(): void {
        if(this.maximumDataValiditySeconds === null)
            return

        const now = Date.now()
        for (const [keyAsString, value] of this.cacheStorage) {
            if ((now - value.timeUsed) > this.maximumDataValiditySeconds * 1000) {
                //log.debug("Cache: Dropping data for key in store " + this.name + " since it is too old: " + keyAsString)
                this.cacheStorage.delete(keyAsString)
                this.objectEntryLocks.delete(keyAsString) 
            }
        }
    }

    private logStatistics(): void {
        if(this.cacheStorage.size === 0)
            return
        log.stat("Cache: Cache stats for " + this.name + ": " + this.requests + " requests, " + Round(this.hits / this.requests * 100, 1) + "% hitrate, " + this.cacheStorage.size + " entries")
        this.hits = 0
        this.requests = 0
    }
}

interface CacheStorage<T, StateType> {
    timeSet: number
    timeUsed: number
    value: T
    stateWhenValid: StateType | null
}

